/**
 * Orders (JwoStoreDSMerchantPortalService) CrossFrame Listeners
 *
 * Handles CrossFrame requests for the Orders page of Merchant Portal V2. Each listener delegates to the
 * gateway client via getServiceClient(); the gateway authorizes every call with a per-store ViewOrders
 * check, so nothing here decides who may see what.
 *
 * The request and response types come from the Orders service's own TypeScript client, the same way
 * Store Health's come from JwoStoreHealthServiceTypescriptClient.
 *
 * Validation is deliberately thin. `storeIds` is optional on the list operations: absent or empty means
 * "every store I may view" and the gateway expands it, so it must not be required here. Fields the
 * Orders service itself marks @required (`filter`, `columns`, ...) are left to its validation, which
 * answers with a typed ValidationException the tool can map; a thrown Error here would reach the tool
 * as an untyped failure. The one exception is GetOrderDetail, whose single storeId is what the
 * gateway authorizes against.
 *
 * This intentionally differs from storeHealthListeners, which require storeId on every call: Store
 * Health operations are scoped to one store, while the Orders list operations span every store the
 * caller may view. Do not add a storeIds requirement to match.
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'

import {
    GetAggregationsInput,
    GetAggregationsOutput,
    ListOrdersInput,
    ListOrdersOutput,
    GetOrderDetailInput,
    GetOrderDetailOutput,
    GetExportOptionsInput,
    GetExportOptionsOutput,
    ExportOrdersInput,
    ExportOrdersOutput,
} from '@amzn/coral_com-amazon-jwostoredsmerchantportalservice'
import { validateRequired } from './listenerUtils'

// ========================================
// READ OPERATIONS (ViewOrders)
// ========================================

/**
 * The summary tiles (trips and payments) for the current filter.
 * Optional: storeIds (empty means every store the caller may view).
 */
export async function getAggregationsListener (message: any): Promise<GetAggregationsOutput> {
    const params = (message && message.data) || {}
    const client = getServiceClient()
    return client.getAggregations(GetAggregationsInput.clone(params)).toPromise()
}

/**
 * One block of order rows plus the total count.
 * Optional: storeIds (empty means every store the caller may view).
 */
export async function listOrdersListener (message: any): Promise<ListOrdersOutput> {
    const params = (message && message.data) || {}
    const client = getServiceClient()
    return client.listOrders(ListOrdersInput.clone(params)).toPromise()
}

/**
 * Full detail for one shopper trip.
 * Required: sessionId, storeId (storeId is what the gateway authorizes against).
 */
export async function getOrderDetailListener (message: any): Promise<GetOrderDetailOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'sessionId', 'storeId')
    const client = getServiceClient()
    return client.getOrderDetail(GetOrderDetailInput.clone(params)).toPromise()
}

/**
 * The export dialog options: selectable columns per view.
 * Optional: storeIds (empty means every store the caller may view).
 */
export async function getExportOptionsListener (message: any): Promise<GetExportOptionsOutput> {
    const params = (message && message.data) || {}
    const client = getServiceClient()
    return client.getExportOptions(GetExportOptionsInput.clone(params)).toPromise()
}

/**
 * Generates the report(s) and returns short-lived presigned download link(s); no file bytes pass
 * through the gateway or this listener.
 * Optional: storeIds (empty means every store the caller may view).
 */
export async function exportOrdersListener (message: any): Promise<ExportOrdersOutput> {
    const params = (message && message.data) || {}
    const client = getServiceClient()
    return client.exportOrders(ExportOrdersInput.clone(params)).toPromise()
}
